/** The catalog records' shared words (plan §2 "Shared words", the One-Wording Rule): where
 *  a catalog object came from, whether an itinerary is live and what its app button does,
 *  how far a saved edit reaches the app, a used-by count the database cancelled, and visit
 *  durations. The activity, category and
 *  itinerary records (header chips, fact band, ledger lines) and the catalog lists import
 *  these sentences; none of them re-words a verdict.
 *
 *  Pure, over minimal local input shapes rather than the API types. The detail payloads
 *  (plan §9.5) fit them as sent — `source` is a `CatalogSource`, `app_reach` an `AppReach`,
 *  `operator_cta_default` is `ctaVerdict`'s operator default — or through a part's own
 *  mapping (the itinerary band's `liveFacts` / `ctaFacts`). A list row carries less, so it
 *  has its own forms of the same words: `catalogSourceName` (the source's name alone),
 *  `itineraryRowStatus` (`default_missing`) and `ctaBehaviourText` (the behaviour alone).
 *  `now` is a parameter. */
import { CTA_BUTTON_BEHAVIOR, CTA_TOUR_DEPARTURE } from '@/api/itineraries';
import { formatRelative, formatZoned } from '@/utils/zonedTime';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "a", "a and b", "a, b and c". */
export function joinAnd(words: string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

// ---------------------------------------------------------------------------
// Status (the header chip)
// ---------------------------------------------------------------------------

/** A catalog object's state in the app. Never "Draft": that word means an unsaved edit.
 *  `coming_soon`: a published itinerary without its catalog itinerary — the app lists it
 *  as "Coming soon"; only `liveVerdict`, which knows that fact, says it. */
export type CatalogStatus = 'live' | 'coming_soon' | 'not_published' | 'placeholder';

export const CATALOG_STATUS_LABEL: Readonly<Record<CatalogStatus, string>> = {
  live: 'Live',
  coming_soon: 'Coming soon',
  not_published: 'Not published',
  placeholder: 'Placeholder',
};

/** An operator's placeholder itinerary (`placeholder: true`, the itinerary its new tours
 *  start from) named in full: the band's datum, the record's note, a refused delete's
 *  reason. The status chip keeps its first word. */
export const PLACEHOLDER_LABEL = 'Placeholder for new tours';

/** A placeholder in the app: `liveVerdict`'s sentence, and the record's placeholder banner. */
export const PLACEHOLDER_VERDICT = `${PLACEHOLDER_LABEL} — not shown in the app`;

/** Why a placeholder's name, published state and deep link cannot change (O1, O9; F41:
 *  renaming or publishing one makes the next new tour mint another placeholder): its
 *  Actions' disabled items and its Name and Published fields' caption. */
export const PLACEHOLDER_LOCKED = 'Locked on a placeholder: new tours of this operator point at it';

/** Why a batch Delete is unavailable while the selection holds placeholders. */
export function placeholderDeleteReason(ids: readonly number[]): string {
  const list = ids.map((id) => `#${id}`).join(', ');
  return `Placeholders for new tours can’t be deleted (${list}). Deselect ${ids.length === 1 ? 'it' : 'them'} first.`;
}

/** A placeholder (the itinerary new tours of an operator point at) is never shown in the
 *  app, whatever its `published` flag says. Without the catalog itinerary fact; a record
 *  takes `liveVerdict(...).status` and a list row `itineraryRowStatus`, which add
 *  `coming_soon`. */
export function catalogStatus(o: {
  published: boolean;
  placeholder?: boolean;
}): Exclude<CatalogStatus, 'coming_soon'> {
  if (o.placeholder) return 'placeholder';
  return o.published ? 'live' : 'not_published';
}

/** A published itinerary group without its catalog itinerary (day plan): the app lists it
 *  as "Coming soon". The record's banner and the list's Catalog itinerary column say it in
 *  these words; `liveVerdict` ends with them. */
export const NO_CATALOG_ITINERARY = 'No catalog itinerary yet';

/** An itinerary list row's status chip — the record header's, from the row's own facts
 *  (`default_missing` is the record's `default_itinerary.id === null`): equal to
 *  `liveVerdict(...).status` for the same itinerary. */
export function itineraryRowStatus(row: {
  published: boolean;
  placeholder: boolean;
  default_missing: boolean;
}): CatalogStatus {
  const status = catalogStatus(row);
  return status === 'live' && row.default_missing ? 'coming_soon' : status;
}

// ---------------------------------------------------------------------------
// Source
// ---------------------------------------------------------------------------

/** The newest import-source run that touched the object (plan §7's evidence chain). */
export interface CatalogSource {
  /** The import source's name ("FareHarbor"). */
  name: string;
  /** That run was an AI import (`Import_Run.ORIGIN_AI_IMPORT`): named by its date. */
  ai_import?: boolean;
  /** When that run ran (ISO); null when unknown. */
  last_run_at: string | null;
  /** The source's `allow_import` update flag for this kind (`update_activities` /
   *  `update_itineraries`): its next sync updates the record. Off = a create-only source. */
  updates: boolean;
}

/** A record no import touched (no `source`, a list row's `source_name` null). */
export const CREATED_IN_CONSOLE = 'Created in the console';

const importedFrom = (name: string) => `Imported from ${name}`;

/** "Imported from FareHarbor" / "Created in the console": `catalogSourceLabel`'s head, for a
 *  list row, which carries the newest import's source name alone (`source_name` — no run
 *  date, no update flag, no AI-import mark). */
export function catalogSourceName(name: string | null | undefined): string {
  const n = name?.trim();
  return n ? importedFrom(n) : CREATED_IN_CONSOLE;
}

/** "Imported from FareHarbor · 2d ago · updates allowed" / "… · create-only source" /
 *  "AI import · 3 Sep" / "Created in the console". */
export function catalogSourceLabel(
  source: CatalogSource | null | undefined,
  now: Date = new Date(),
): string {
  if (!source) return CREATED_IN_CONSOLE;
  if (source.ai_import) {
    return source.last_run_at
      ? `AI import · ${formatZoned(source.last_run_at, 'UTC', { weekday: false, time: false, zone: false, now })}`
      : 'AI import';
  }
  const when = source.last_run_at
    ? ` · ${formatRelative(source.last_run_at, { now, style: 'narrow' })}`
    : '';
  return `${importedFrom(source.name)}${when} · ${source.updates ? 'updates allowed' : 'create-only source'}`;
}

// ---------------------------------------------------------------------------
// Itinerary: in the app
// ---------------------------------------------------------------------------

export interface ItineraryLiveFacts {
  published: boolean;
  placeholder: boolean;
  /** The group has its default (catalog) itinerary; without one the app shows "Coming soon". */
  has_default: boolean;
  show_in_catalog: boolean;
  show_in_region_search: boolean;
  show_in_trip_type_search: boolean;
  show_in_text_search: boolean;
}

export interface LiveVerdict {
  status: CatalogStatus;
  text: string;
}

/** "Live · catalog, region, trip type and text search" / "Not published — hidden in the
 *  app" / "Coming soon · published, but no catalog itinerary yet" / "Placeholder for new
 *  tours — not shown in the app". The text opens with the status chip's word (the fact
 *  band's datum is its head), so the chip, the band and the banner never disagree. */
export function liveVerdict(it: ItineraryLiveFacts): LiveVerdict {
  const status = catalogStatus(it);
  if (status === 'placeholder') return { status, text: PLACEHOLDER_VERDICT };
  if (status === 'not_published') return { status, text: 'Not published — hidden in the app' };
  if (!it.has_default) {
    return {
      status: 'coming_soon',
      text: `Coming soon · published, but ${lowerFirst(NO_CATALOG_ITINERARY)}`,
    };
  }
  const searches = [
    it.show_in_region_search && 'region',
    it.show_in_trip_type_search && 'trip type',
    it.show_in_text_search && 'text',
  ].filter((w): w is string => !!w);
  const words = [...(it.show_in_catalog ? ['catalog'] : []), ...searches];
  if (!words.length) return { status, text: 'Live · not in the catalog or any search' };
  return { status, text: `Live · ${joinAnd(words)}${searches.length ? ' search' : ''}` };
}

// ---------------------------------------------------------------------------
// Itinerary: the app button
// ---------------------------------------------------------------------------

/** The field the two link behaviours open and the Phone behaviour dials: its label where
 *  it is edited, and its name in every sentence about the button. */
export const BOOKING_URL = 'Booking URL';

/** What the Phone behaviour needs in the Booking URL: a `tel:` URI (the XML importer and
 *  exporter write `tel:<number>`). */
export const TEL_LINK = 'tel: link';

const TEL_LINK_EXAMPLE = 'tel:+442079460000';

/** The Booking URL's caption while the behaviour is Phone: what the button does with it. */
export const PHONE_BOOKING_URL_HINT = `The Phone Number button dials this ${TEL_LINK}, e.g. ${TEL_LINK_EXAMPLE}`;

/** …and while it holds something else (a web address, say). A note, never a save block:
 *  Phone without a number is allowed too. */
export const PHONE_NOT_TEL_NOTE = `This isn’t a ${TEL_LINK}, so the Phone Number button can’t dial it. Enter one like ${TEL_LINK_EXAMPLE}.`;

export interface ItineraryCtaFacts {
  cta_button_text: string | null;
  /** `Tour_Operator.CTA_BEHAVIORS` (0–6); null inherits the operator's behaviour. */
  cta_button_behavior: number | null;
  /** The URL the two link behaviours open; for the Phone behaviour, the `tel:` link it
   *  dials (what the XML importer and exporter write). Unused by the others. */
  booking_url: string | null;
  cta_tour: { name: string } | null;
  /** `Itinerary_Group.CTA_TOUR_DEPARTURES` (0–2). */
  cta_tour_departure: number | null;
}

export interface CtaVerdict {
  /** One sentence: what the button does in the app. */
  text: string;
  /** A stored combination the app cannot act on, as an info note (never red). */
  note: string | null;
  /** The behaviour came from the operator. */
  inherited: boolean;
}

const DEPARTURE_WORDS: Readonly<Record<number, string>> = {
  [CTA_TOUR_DEPARTURE.NEXT]: 'next departure',
  [CTA_TOUR_DEPARTURE.RECENT]: 'most recent departure',
  [CTA_TOUR_DEPARTURE.ASK]: 'asks which departure',
};

/** The number a Phone button dials: the `tel:` URI stored in `booking_url`
 *  ("tel:+1 555 0100" → "+1 555 0100"); null when it holds no usable number. */
export function telNumber(bookingUrl: string | null | undefined): string | null {
  const m = /^tel:(.*)$/i.exec(bookingUrl?.trim() ?? '');
  if (!m) return null;
  let n = m[1] ?? '';
  try {
    n = decodeURIComponent(n);
  } catch {
    // Not percent-encoded: shown as stored.
  }
  n = n.trim();
  return /\d/.test(n) ? n : null;
}

function behaviourSentence(
  behavior: number,
  it: ItineraryCtaFacts,
): { text: string; note: string | null } {
  const url = it.booking_url?.trim() || null;
  const tour = it.cta_tour?.name.trim() || null;
  const departure =
    DEPARTURE_WORDS[it.cta_tour_departure ?? CTA_TOUR_DEPARTURE.NEXT] ?? 'next departure';
  const noUrl = url ? null : `No ${BOOKING_URL} — the button has nothing to open`;
  const noTour = (what: string) => (tour ? null : `No tour — the button has nothing to ${what}`);
  switch (behavior) {
    case CTA_BUTTON_BEHAVIOR.LINK_INAPP:
      return { text: `Opens the ${BOOKING_URL} in the in-app browser`, note: noUrl };
    case CTA_BUTTON_BEHAVIOR.LINK_BROWSER:
      return { text: `Opens the ${BOOKING_URL} in the phone’s browser`, note: noUrl };
    case CTA_BUTTON_BEHAVIOR.PHONE: {
      const number = telNumber(url);
      return number
        ? { text: `Dials ${number}`, note: null }
        : {
            text: `Dials the ${TEL_LINK} in the ${BOOKING_URL}`,
            note: `No ${TEL_LINK} in the ${BOOKING_URL} — the button has nothing to dial`,
          };
    }
    case CTA_BUTTON_BEHAVIOR.CHAT:
      return { text: 'Opens chat with the assistant', note: null };
    case CTA_BUTTON_BEHAVIOR.ADD_TRIP:
      return {
        text: tour ? `Adds “${tour}” to My Trips (${departure})` : 'Adds a tour to My Trips',
        note: noTour('add'),
      };
    case CTA_BUTTON_BEHAVIOR.EXPLORE:
      return {
        text: tour ? `Shows “${tour}” on Explore` : 'Shows a tour on Explore',
        note: noTour('show'),
      };
    case CTA_BUTTON_BEHAVIOR.ADD_OR_EXPLORE:
      return {
        text: tour
          ? `Adds “${tour}” to My Trips (${departure}) when signed in, otherwise shows it on Explore`
          : 'Adds a tour to My Trips, or shows it on Explore',
        note: noTour('add'),
      };
    default:
      return { text: `Unknown button behaviour (${behavior})`, note: null };
  }
}

/** Inherits, the operator's behaviour unknown. */
const INHERITS_OPERATOR = 'Inherits the operator’s button behaviour';

/** One sentence per behaviour, "inherit" resolved to the operator's actual behaviour
 *  (`Tour_Operator.cta_button_behavior`; `operatorDefault` null when unknown). The app
 *  shows the button only when its text is set — the itinerary's own text, never the
 *  operator's. */
export function ctaVerdict(it: ItineraryCtaFacts, operatorDefault: number | null): CtaVerdict {
  const inherited = it.cta_button_behavior === null;
  if (!it.cta_button_text?.trim()) {
    return { text: 'No button: the button text is empty', note: null, inherited };
  }
  const behavior = it.cta_button_behavior ?? operatorDefault;
  if (behavior === null) return { text: INHERITS_OPERATOR, note: null, inherited };
  const { text, note } = behaviourSentence(behavior, it);
  return { text: inherited ? `Inherits the operator: ${lowerFirst(text)}` : text, note, inherited };
}

const NO_TARGET: ItineraryCtaFacts = {
  cta_button_text: null,
  cta_button_behavior: null,
  booking_url: null,
  cta_tour: null,
  cta_tour_departure: null,
};

/** What a behaviour does, without the itinerary's Booking URL, tour or button text: a list
 *  row's App button cell, which carries `cta_button_behavior` alone (null inherits the
 *  operator's). `ctaVerdict` says the same sentence with its target filled in: "Opens the
 *  Booking URL in the in-app browser", "Adds a tour to My Trips", "Inherits the operator’s
 *  button behaviour". */
export function ctaBehaviourText(behavior: number | null): string {
  return behavior === null ? INHERITS_OPERATOR : behaviourSentence(behavior, NO_TARGET).text;
}

// ---------------------------------------------------------------------------
// App reach (D8: say it, don't fix it)
// ---------------------------------------------------------------------------

export type CatalogKind = 'activity' | 'itinerary';

/** An activity's reach into the app, from its edit context (plan §8). */
export interface AppReach {
  /** `get_cascade_flush_mode()`: `full` / `best_effort` refresh tour caches in the
   *  background; `paused` (and anything unknown) does not. */
  cascade_mode: string;
  /** The operator ships every activity type in its manifest: the activity's own app entry
   *  updates in ~3 s. */
  manifest_all_types: boolean;
  /** Upcoming tours that carry the activity: the used-by summary's `tours_upcoming` (edit_context
   *  sends it while the summary is remembered; the band passes the summary's own). Absent or null
   *  when not counted. */
  tours_upcoming?: number | null;
}

/** The ledger's app-reach line and the band's "Used by" detail. */
export function appReachLine(kind: CatalogKind, reach?: AppReach | null): string {
  if (kind === 'itinerary') {
    return 'The app’s catalog entry refreshes in ~3 s · trips already added keep their own days (highlights update everywhere)';
  }
  const n = reach?.tours_upcoming;
  const refresh = !reach
    ? ''
    : reach.cascade_mode === 'full' || reach.cascade_mode === 'best_effort'
      ? ' in the background'
      : ' (refresh paused)';
  const tours =
    n === 0
      ? 'No upcoming tour carries this activity'
      : `Travellers on ${typeof n === 'number' ? plural(n, 'upcoming tour') : 'upcoming tours'} keep the previous version until the tour cache refreshes${refresh}`;
  return reach?.manifest_all_types
    ? `${tours} · the activity’s own app entry updates in ~3 s`
    : tours;
}

/** Why the database cancelled a used-by summary's read (its `reason` with `available: false`). */
export type SummaryUnavailableReason = 'timeout' | 'replica_conflict' | null;

/** A used-by summary the database cancelled, said where its count would be: "Tours not
 *  counted (timed out)". The band keeps the detail's counts beside it; the server remembers
 *  the miss for 2 minutes, so a later visit counts again. */
export function notCounted(what: string, reason: SummaryUnavailableReason): string {
  const why =
    reason === 'timeout'
      ? ' (timed out)'
      : reason === 'replica_conflict'
        ? ' (cancelled by the replica)'
        : '';
  return `${what} not counted${why}`;
}

// ---------------------------------------------------------------------------
// Durations (stored in hours)
// ---------------------------------------------------------------------------

// One function each, shared with the record editor's duration widget (records/time/hours).
export { formatHours, formatHoursRange, joinHours, splitHours } from '@/features/records/time/hours';
